import { api } from '../lib/api';
import type { AttachmentView } from '../lib/api';
import { formatBytes } from '../lib/format';
import styles from './AttachmentStrip.module.css';

export interface AttachmentStripProps {
  /** `MessageView.id`, which is what `/api/attachments/:messageId/:partId` keys on. */
  messageId: number;
  attachments: AttachmentView[];
  className?: string;
}

function ImageIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2.2" y="3.2" width="11.6" height="9.6" rx="1.6" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="6" cy="6.4" r="1" fill="currentColor" />
      <path d="m3 11.4 3.2-3 2.2 2 2-1.6 2.6 2.6" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 2.2h5l3 3v8.6H4z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M9 2.2v3h3M5.8 8.4h4.4M5.8 10.6h4.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ArchiveFileIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2.6" y="2.6" width="10.8" height="10.8" rx="1.6" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7.4 2.8v2.4m1.2 0V7.6m-1.2 0V10m1.2 0v1.6H7.4V10" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function iconFor(mimeType: string) {
  if (mimeType.startsWith('image/')) return <ImageIcon />;
  if (/zip|compressed|tar|gzip|7z/.test(mimeType)) return <ArchiveFileIcon />;
  return <DocIcon />;
}

export function AttachmentStrip({ messageId, attachments, className }: AttachmentStripProps) {
  // Inline parts already appear inside the body; listing them again is noise.
  const visible = attachments.filter((a) => !(a.inline && a.contentId));
  if (visible.length === 0) return null;

  return (
    <div className={`${styles.strip} ${className ?? ''}`}>
      {visible.map((attachment) => {
        const name = attachment.filename || `part-${attachment.partId}`;
        return (
          <a
            key={attachment.id}
            className={styles.chip}
            href={api.attachmentUrl(messageId, attachment.partId)}
            download={name}
            title={`${name} · ${formatBytes(attachment.size)}`}
          >
            <span className={styles.icon}>{iconFor(attachment.mimeType)}</span>
            <span className={styles.meta}>
              <span className={styles.name}>{name}</span>
              <span className={styles.size}>{formatBytes(attachment.size)}</span>
            </span>
          </a>
        );
      })}
    </div>
  );
}

export default AttachmentStrip;
